/**
 * Downloads a file from a URL (works for cross-origin files)
 * @param {string} fileUrl - The URL of the file to download
 * @param {string} fileName - The name to save the file as (optional)
 * @returns {Promise<void>}
 */
export const downloadFile = async (fileUrl, fileName = "document") => {
  if (!fileUrl) {
    console.warn("downloadFile: No file URL provided")
    return
  }

  try {
    const response = await fetch(fileUrl)
    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)

    const link = document.createElement("a")
    link.href = blobUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()

    link.remove()
    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error("Error downloading file:", error)
    window.open(fileUrl, "_blank")
  }
}

/**
 * Validates file type and size
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @param {string[]} options.acceptedTypes - Array of accepted MIME types
 * @param {number} options.maxSizeMB - Maximum file size in MB
 * @returns {{ isValid: boolean, error: string | null }}
 */
export const validateFile = (file, options = {}) => {
  const { acceptedTypes = [], maxSizeMB = 5 } = options

  if (acceptedTypes.length > 0 && !acceptedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `Invalid file type. Accepted types: ${acceptedTypes.join(", ")}`
    }
  }

  const fileSizeMB = file.size / 1024 / 1024
  if (fileSizeMB > maxSizeMB) {
    return {
      isValid: false,
      error: `File must be smaller than ${maxSizeMB}MB`
    }
  }

  return { isValid: true, error: null }
}

/**
 * Formats file size to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size (e.g., "2.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes"

  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

/**
 * Gets file extension from filename
 * @param {string} filename - The filename
 * @returns {string} - File extension (e.g., "pdf")
 */
export const getFileExtension = (filename) => {
  if (!filename) return ""
  const parts = filename.split(".")
  return parts.length > 1 ? parts.pop().toLowerCase() : ""
}
